import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, AlertCircle } from "lucide-react";
import { useEventStudentSearch } from "@/hooks/events/useFishsixStudentSearch";
import { useAssignStudent } from "@/hooks/events/useAssignStudent";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventId: string;
}

export const AssignStudentDialog = ({ open, onOpenChange, eventId }: Props) => {
  const [search, setSearch] = useState("");
  const { data: results = [], isFetching } = useEventStudentSearch(search);
  const assign = useAssignStudent();

  const handleConfirm = async (code: string, kind: "fs" | "esc" | "kn", balance: number) => {
    if (balance <= 0) return;
    await assign.mutateAsync({ eventId, studentCode: code, studentKind: kind });
    onOpenChange(false);
    setSearch("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setSearch(""); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign นักเรียน (Fishsix / ESC)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="ค้นหาด้วยรหัส / ชื่อ / นามสกุล / ชื่อเล่น"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              autoFocus
            />
          </div>

          <div className="max-h-96 overflow-auto border rounded-md divide-y">
            {search.trim().length === 0 && (
              <div className="text-sm text-muted-foreground p-4 text-center">
                พิมพ์เพื่อค้นหานักเรียน
              </div>
            )}
            {search.trim().length > 0 && isFetching && (
              <div className="text-sm text-muted-foreground p-4 text-center">กำลังค้นหา...</div>
            )}
            {search.trim().length > 0 && !isFetching && results.length === 0 && (
              <div className="text-sm text-muted-foreground p-4 text-center">ไม่พบนักเรียน</div>
            )}
            {results.map((r) => {
              const noBalance = r.event_balance <= 0;
              return (
                <div key={`${r.kind}-${r.code}`} className="flex items-center justify-between p-3 gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {r.first_name ?? ""} {r.last_name ?? ""}
                      {r.nickname ? ` (${r.nickname})` : ""}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <span className="uppercase mr-1">[{r.kind}]</span>
                      {r.code} · คงเหลือ:{" "}
                      <span className={noBalance ? "text-destructive font-medium" : "font-medium"}>
                        {r.event_balance} ครั้ง
                      </span>
                    </div>
                    {noBalance && (
                      <div className="text-xs text-destructive flex items-center gap-1 mt-1">
                        <AlertCircle className="h-3 w-3" />
                        ไม่มีคงเหลือ assign ไม่ได้
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    disabled={noBalance || assign.isPending}
                    onClick={() => handleConfirm(r.code, r.kind, r.event_balance)}
                  >
                    เลือก
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
